import React, { useEffect, useState } from "react";
import { CustomTable } from "../../components/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { POST } from "../../utils/webService";
import { COMPANY_NAME, COURIER_PRICING } from "../../utils/ApiUrls";
import { ScrollNav } from "../../components/ScrollNav";
import upArrowBlue from "../../assets/upArrorwBlue.svg";
import UpArrowIcon from "../../assets/Filter/upArrow.svg";
import DownArrowIcon from "../../assets/Filter/downArrow.svg";
import RateCardTable from "./rateCardTable";
import { capitalizeFirstLetter } from "../../utils/utility";
import { toast } from "react-hot-toast";
import { Spinner } from "../../components/Spinner";
import { useSelector } from "react-redux";

interface ICourierPricingPropTypes {
  logisticsData?: any;
  setLogisticsData?: any;
  isLoading?: boolean;
}

// ========== UTILITY FUNCTION TO FORMAT VARIABLE NAMES ==========
// Converts "appointmentbaseddeliverychargesCharges" to "Appointment Based Delivery Charges"
const formatVariableName = (key: string): string => {
  // Remove trailing "Charges" if duplicated (e.g., "peaksurchargesCharges" -> "peaksurcharges")
  let cleanKey = key.replace(/Charges$/, "");

  // Handle camelCase and lowercase concatenated words
  // Insert space before capital letters
  let formatted = cleanKey.replace(/([A-Z])/g, " $1");

  // Handle fully lowercase strings by inserting spaces at common word boundaries
  const commonWords = [
    "appointment",
    "based",
    "delivery",
    "charges",
    "green",
    "tax",
    "delhi",
    "ncr",
    "holiday",
    "pickup",
    "out",
    "of",
    "area",
    "oda",
    "opa",
    "peak",
    "surcharges",
    "fuel",
    "handling",
    "heavy",
    "shipment",
    "remote",
    "reverse",
    "docket",
    "demurrage",
    "attempt",
    "additional",
    "pod",
    "charge",
    "hard",
    "copies",
    "pay",
    "pincode",
    "adhoc",
    "weight",
    "consignee",
    "copy",
    "multi",
    "story",
    "kerala",
    "storage",
    "facility",
    "damage",
    "claim",
    "currency",
    "adjustment",
    "critical",
    "location",
    "fov",
    "rov",
    "diesel",
    "mechanism",
    "base",
  ];

  // If the string is mostly lowercase, try to split it
  if (formatted === formatted.toLowerCase() || formatted.trim() === cleanKey) {
    let result = cleanKey.toLowerCase();
    // Sort by length descending to match longer words first
    const sortedWords = [...commonWords].sort((a, b) => b.length - a.length);
    sortedWords.forEach((word) => {
      const regex = new RegExp(word, "gi");
      result = result.replace(regex, ` ${word} `);
    });
    formatted = result;
  }

  // Clean up extra spaces and capitalize each word
  return formatted
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// ========== FUNCTION TO EXTRACT VARIABLE KEY AND DATA ==========
const extractVariableData = (variableObj: any): { key: string; data: any } | null => {
  // Get all keys except 'isActive'
  const keys = Object.keys(variableObj).filter((k) => k !== "isActive");
  if (keys.length === 0) return null;

  const key = keys[0]; // The variable name key
  const data = variableObj[key]; // The charge data object

  return { key, data };
};

const CourierPricing = (props: ICourierPricingPropTypes) => {
  const { logisticsData, setLogisticsData, isLoading } = props;

  const columnsHelper = createColumnHelper<any>();

  const [data, setData] = useState<any>({ b2bData: [], b2cData: [] });
  const [renderingComponents, setRenderingComponents] = useState(0);
  const isMasked = useSelector((state: any) => state?.user?.isMasked);

  const [codInfo, setCodInfo] = useState({
    codPercentage: 0,
    codCharges: 0,
    codChargePreference: "",
  });

  // ========== DYNAMIC VARIABLE COLUMNS ==========
  const variableColumns = [
    columnsHelper.accessor("termType", {
      header: () => {
        return (
          <div className="flex items-center min-w-[200px]">
            <h1 className="text-sm font-semibold leading-5">Term Type</h1>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const variableObj = row?.original;
        const extracted = extractVariableData(variableObj);

        if (!extracted) return <div>-</div>;

        const formattedName = formatVariableName(extracted.key);

        return (
          <div className="flex items-center my-4 font-medium">
            {formattedName}
          </div>
        );
      },
    }),
    columnsHelper.accessor("definition", {
      header: () => {
        return (
          <div className="flex justify-between items-center min-w-[400px]">
            <h1 className="text-sm font-semibold leading-5">Definition</h1>
          </div>
        );
      },
      cell: ({ row }: any) => {
        const variableObj = row?.original;
        const extracted = extractVariableData(variableObj);

        if (!extracted) return <div>-</div>;

        const chargeData = extracted.data;
        console.log("chargeData", chargeData);

        return (
          <div className="py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Absolute Charge: </span>
                <span className="font-medium">₹{chargeData?.absoluteCharge || 0}</span>
              </div>
              <div>
                <span className="text-gray-500">Percentage: </span>
                <span className="font-medium">{chargeData?.percentageCharge || 0}%</span>
              </div>
              <div>
                <span className="text-gray-500">Calculate On: </span>
                <span className="font-medium">{chargeData?.calculateOn || "N/A"}</span>
              </div>
              <div>
                <span className="text-gray-500">Preference: </span>
                <span className="font-medium">{chargeData?.preferenceTo || "N/A"}</span>
              </div>
            </div>
          </div>
        );
      },
    }),
  ];

  const arrayData = [
    { index: 0, label: "B2C" },
    { index: 1, label: "B2B" },
  ];

  const setScrollIndex = (id: number) => {
    setRenderingComponents(id);
  };

  const RatesB2CForpartners: any = () => {
    let logisticsB2CData: any = logisticsData?.filter(
      (el: any) => el?.accountType === "B2C"
    );

    return logisticsB2CData?.map((logisticsInfo: any, index: number) => {
      // Filter active variables
      const filterVariableData = logisticsInfo?.variables?.filter(
        (obj: any) => obj?.isActive === true
      ) || [];

      return (
        <div className="mt-5" key={index}>
          {logisticsInfo?.rates?.map((rateCard: any, rateIndex: number) => {
            if (rateCard?.isActive === true) {
              return (
                <div className="flex flex-col mb-2" key={rateIndex}>
                  <div
                    className={`grid grid-cols-2 px-4 h-[50px] gap-y-6 cursor-pointer
                      ${
                        rateCard?.isCollapse
                          ? "bg-[#E8E8E8] rounded-tr-lg rounded-tl-lg border-[1px]"
                          : "shadow-md rounded"
                      }
                    `}
                    onClick={() => {
                      let temp = [...logisticsData];
                      rateCard.isCollapse = !rateCard.isCollapse;
                      setLogisticsData(temp);
                    }}
                  >
                    <div className="flex items-center gap-x-2">
                      <h1 className="self-center justify-start text-[16px] font-semibold font-Lato text-[#1C1C1C] whitespace-nowrap">
                        {capitalizeFirstLetter(rateCard.partnerName)}
                      </h1>
                    </div>
                    <div className="flex justify-end items-center gap-x-1">
                      <img
                        src={rateCard.isCollapse ? UpArrowIcon : DownArrowIcon}
                        alt=""
                        className="cursor-pointer"
                      />
                    </div>
                  </div>
                  {rateCard.isCollapse && (
                    <div className="overflow-auto">
                      <RateCardTable serviceData={rateCard?.service} />
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}

          {/* COD Handling Charges */}
          <div className="w-full mb-4">
            <div className="p-4 bg-white rounded-lg shadow-md">
              <h3 className="text-[22px] font-Lato font-semibold mb-3">
                COD Handling Charges
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Percentage</p>
                  <p className="font-semibold text-lg">
                    {logisticsInfo.codPercentage || 0}%
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Unit Price</p>
                  <p className="font-semibold text-lg">
                    ₹ {logisticsInfo.codCharges || 0}
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Condition</p>
                  <p className="font-semibold text-lg">
                    {capitalizeFirstLetter(
                      logisticsInfo.codChargePreference || "higher"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Variable Charges - Now Dynamic! */}
          {filterVariableData.length > 0 && (
            <div className="ml-4 mt-6">
              <h4 className="text-[22px] font-Lato font-semibold">
                Variable Charges
              </h4>
              <div className="mt-7">
                <CustomTable
                  rowData={filterVariableData}
                  columnsData={variableColumns}
                />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const RatesB2CForMaskedpartners: any = () => {
    let logisticsB2CData: any = logisticsData?.filter(
      (el: any) => el?.accountType === "B2C"
    );

    return logisticsB2CData?.map((logisticsInfo: any, index: number) => {
      let firstB2CObject = logisticsInfo?.rates?.[0];

      // Filter active variables
      const filterVariableData = logisticsInfo?.variables?.filter(
        (obj: any) => obj?.isActive === true
      ) || [];

      let serviceArray = firstB2CObject?.service?.slice(0, 2);
      serviceArray?.forEach((element: any, i: number) => {
        if (i < 2) {
          if (element?.mode === "AIR") {
            element.partnerServiceName = "Air";
          } else if (element?.mode === "SURFACE") {
            element.partnerServiceName = "SURFACE";
          }
        }
      });

      return (
        <div className="mt-5" key={index}>
          <div className="flex flex-col mb-2">
            <div
              className={`grid grid-cols-2 px-4 h-[50px] gap-y-6 cursor-pointer
                ${
                  firstB2CObject?.isCollapse
                    ? "bg-[#E8E8E8] rounded-tr-lg rounded-tl-lg border-[1px]"
                    : "shadow-md rounded"
                }
              `}
              onClick={() => {
                let temp = [...logisticsData];
                firstB2CObject.isCollapse = !firstB2CObject.isCollapse;
                setLogisticsData(temp);
              }}
            >
              <div className="flex items-center gap-x-2">
                <h1 className="self-center justify-start text-[16px] font-semibold font-Lato text-[#1C1C1C] whitespace-nowrap">
                  {capitalizeFirstLetter(COMPANY_NAME)}
                </h1>
              </div>
              <div className="flex justify-end items-center gap-x-1">
                <img
                  src={firstB2CObject?.isCollapse ? UpArrowIcon : DownArrowIcon}
                  alt=""
                  className="cursor-pointer"
                />
              </div>
            </div>
            {firstB2CObject?.isCollapse && (
              <div className="overflow-auto">
                <RateCardTable serviceData={serviceArray} />
              </div>
            )}
          </div>

          {/* COD Handling Charges */}
          <div className="w-full mb-4">
            <div className="p-4 bg-white rounded-lg shadow-md">
              <h3 className="text-[22px] font-Lato font-semibold mb-3">
                COD Handling Charges
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Percentage</p>
                  <p className="font-semibold text-lg">
                    {logisticsInfo.codPercentage || 0}%
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Unit Price</p>
                  <p className="font-semibold text-lg">
                    ₹ {logisticsInfo.codCharges || 0}
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Condition</p>
                  <p className="font-semibold text-lg">
                    {capitalizeFirstLetter(
                      logisticsInfo.codChargePreference || "higher"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Variable Charges - Now Dynamic! */}
          {filterVariableData.length > 0 && (
            <div className="ml-4 mt-6">
              <h4 className="text-[22px] font-Lato font-semibold">
                Variable Charges
              </h4>
              <div className="mt-7">
                <CustomTable
                  rowData={filterVariableData}
                  columnsData={variableColumns}
                />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const RatesB2BForpartners: any = () => {
    let logisticsB2BData: any = logisticsData?.filter(
      (el: any) => el?.accountType === "B2B"
    );

    return logisticsB2BData?.map((logisticsInfo: any, index: number) => {
      // Filter active variables
      const filterVariableData = logisticsInfo?.variables?.filter(
        (obj: any) => obj?.isActive === true
      ) || [];

      return (
        <div className="mt-5" key={index}>
          {logisticsInfo?.rates?.map((rateCard: any, rateIndex: number) => {
            if (rateCard?.isActive === true) {
              return (
                <div className="flex flex-col mb-2" key={rateIndex}>
                  <div
                    className={`grid grid-cols-2 px-4 h-[50px] gap-y-6 cursor-pointer
                      ${
                        rateCard?.isCollapse
                          ? "bg-[#E8E8E8] rounded-tr-lg rounded-tl-lg border-[1px]"
                          : "shadow-md rounded"
                      }
                    `}
                    onClick={() => {
                      let temp = [...logisticsData];
                      rateCard.isCollapse = !rateCard.isCollapse;
                      setLogisticsData(temp);
                    }}
                  >
                    <div className="flex items-center gap-x-2">
                      <h1 className="self-center justify-start text-[16px] font-semibold font-Lato text-[#1C1C1C] whitespace-nowrap">
                        {capitalizeFirstLetter(rateCard.partnerName)}
                      </h1>
                    </div>
                    <div className="flex justify-end items-center gap-x-1">
                      <img
                        src={rateCard.isCollapse ? UpArrowIcon : DownArrowIcon}
                        alt=""
                        className="cursor-pointer"
                      />
                    </div>
                  </div>
                  {rateCard.isCollapse && (
                    <div className="overflow-auto">
                      <RateCardTable serviceData={rateCard?.service} />
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}

          {/* COD Handling Charges */}
          <div className="w-full mb-4">
            <div className="p-4 bg-white rounded-lg shadow-md">
              <h3 className="text-[22px] font-Lato font-semibold mb-3">
                COD Handling Charges
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Percentage</p>
                  <p className="font-semibold text-lg">
                    {logisticsInfo.codPercentage || 0}%
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Unit Price</p>
                  <p className="font-semibold text-lg">
                    ₹ {logisticsInfo.codCharges || 0}
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Condition</p>
                  <p className="font-semibold text-lg">
                    {capitalizeFirstLetter(
                      logisticsInfo.codChargePreference || "higher"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Variable Charges - Now Dynamic! */}
          {filterVariableData.length > 0 && (
            <div className="ml-4 mt-6">
              <h4 className="text-[22px] font-Lato font-semibold">
                Variable Charges
              </h4>
              <div className="mt-7">
                <CustomTable
                  rowData={filterVariableData}
                  columnsData={variableColumns}
                />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const RatesB2BForMaskedpartners: any = () => {
    let logisticsB2BData: any = logisticsData?.filter(
      (el: any) => el?.accountType === "B2B"
    );

    return logisticsB2BData?.map((logisticsInfo: any, index: number) => {
      let firstB2BObject = logisticsInfo?.rates?.[0];

      // Filter active variables
      const filterVariableData = logisticsInfo?.variables?.filter(
        (obj: any) => obj?.isActive === true
      ) || [];

      let serviceArray = firstB2BObject?.service?.slice(0, 2);
      serviceArray?.forEach((element: any, i: number) => {
        if (i < 2) {
          if (element?.mode === "AIR") {
            element.partnerServiceName = "Air";
          } else if (element?.mode === "SURFACE") {
            element.partnerServiceName = "SURFACE";
          }
        }
      });

      return (
        <div className="mt-5" key={index}>
          <div className="flex flex-col mb-2">
            <div
              className={`grid grid-cols-2 px-4 h-[50px] gap-y-6 cursor-pointer
                ${
                  firstB2BObject?.isCollapse
                    ? "bg-[#E8E8E8] rounded-tr-lg rounded-tl-lg border-[1px]"
                    : "shadow-md rounded"
                }
              `}
              onClick={() => {
                let temp = [...logisticsData];
                firstB2BObject.isCollapse = !firstB2BObject.isCollapse;
                setLogisticsData(temp);
              }}
            >
              <div className="flex items-center gap-x-2">
                <h1 className="self-center justify-start text-[16px] font-semibold font-Lato text-[#1C1C1C] whitespace-nowrap">
                  {capitalizeFirstLetter(COMPANY_NAME)}
                </h1>
              </div>
              <div className="flex justify-end items-center gap-x-1">
                <img
                  src={firstB2BObject?.isCollapse ? UpArrowIcon : DownArrowIcon}
                  alt=""
                  className="cursor-pointer"
                />
              </div>
            </div>
            {firstB2BObject?.isCollapse && (
              <div className="overflow-auto">
                <RateCardTable serviceData={serviceArray} />
              </div>
            )}
          </div>

          {/* COD Handling Charges */}
          <div className="w-full mb-4">
            <div className="p-4 bg-white rounded-lg shadow-md">
              <h3 className="text-[22px] font-Lato font-semibold mb-3">
                COD Handling Charges
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Percentage</p>
                  <p className="font-semibold text-lg">
                    {logisticsInfo.codPercentage || 0}%
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Unit Price</p>
                  <p className="font-semibold text-lg">
                    ₹ {logisticsInfo.codCharges || 0}
                  </p>
                </div>
                <div className="p-3 border border-[#E8E8E8] rounded">
                  <p className="text-sm text-gray-500 mb-1">Condition</p>
                  <p className="font-semibold text-lg">
                    {capitalizeFirstLetter(
                      logisticsInfo.codChargePreference || "higher"
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Variable Charges - Now Dynamic! */}
          {filterVariableData.length > 0 && (
            <div className="ml-4 mt-6">
              <h4 className="text-[22px] font-Lato font-semibold">
                Variable Charges
              </h4>
              <div className="mt-7">
                <CustomTable
                  rowData={filterVariableData}
                  columnsData={variableColumns}
                />
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  useEffect(() => {
    (async () => {
      const { data } = await POST(COURIER_PRICING);
      if (data?.success) {
        setData(data?.data);
      } else {
        toast.error(data.message);
      }
    })();
  }, [renderingComponents]);

  function getComponentToRender(renderingComponents: any, isMasked: any) {
    if (renderingComponents === 0) {
      return isMasked ? <RatesB2CForMaskedpartners /> : <RatesB2CForpartners />;
    } else if (renderingComponents === 1) {
      return isMasked ? <RatesB2BForMaskedpartners /> : <RatesB2BForpartners />;
    }
    return null;
  }

  useEffect(() => {
    if (logisticsData && logisticsData.length > 0) {
      const accountType = renderingComponents === 0 ? "B2C" : "B2B";
      const filteredData = logisticsData.find(
        (data: any) => data?.accountType === accountType
      );

      if (filteredData) {
        setCodInfo({
          codPercentage: filteredData.codPercentage || 0,
          codCharges: filteredData.codCharges || 0,
          codChargePreference: filteredData.codChargePreference || "HIGHER",
        });
      }
    }
  }, [logisticsData, renderingComponents]);

  return (
    <div>
      <div className="mx-5 lg:ml-[20px]">
        <ScrollNav
          arrayData={arrayData}
          showNumber={false}
          setScrollIndex={setScrollIndex}
        />
      </div>

      <div className="mx-5 lg:ml-[20px] mb-[68px] customScroll">
        {isLoading ? (
          <div className="flex justify-center items-center h-40">
            <Spinner />
          </div>
        ) : (
          getComponentToRender(renderingComponents, isMasked)
        )}
      </div>
    </div>
  );
};

export default CourierPricing;
